import { GameServices } from "game/GameServices";
import { View } from "@westpierstudio/luma/mvc";
import { Orientation } from "@westpierstudio/luma/layout";
import { Tween } from "@westpierstudio/luma/createjs";
import { Signal } from "type-signals";
import { Text } from "pixi.js";
import { AdvancedText } from "@westpierstudio/luma/utils/advancedtext";
import { BoundedContainer } from "utils/BoundedContainer";

class FreeSpinCounterSignals {
    totalCounterIncrement = new Signal();
    totalCountUpComplete = new Signal();
}

export class FreeSpinCounter extends View {
    private readonly threeDigitScale!: number;
    private spec: Record<string, any>;
    private currentValue!: Text | AdvancedText;
    private totalValue!: Text | AdvancedText;
    signals = new FreeSpinCounterSignals();

    constructor(services: GameServices) {
        super(services);

        const {factory, resources} = this.services;

        this.spec = resources.getSpec(`freespincounter`);
        this.threeDigitScale = resources.getSpec(`freespincounter`).threeDigitScale;

        factory.createSprite(this.spec.panel, this);
        const headerBounds = this.addChild(new BoundedContainer(this.spec.freeSpinCounterHeaderBounds));
        factory.applySpec(headerBounds, this.spec.freeSpinCounterHeaderBounds);
        factory.createText(resources.getMessage(`freeSpinCounterHeader`), this.spec.freeSpinCounterHeader, headerBounds);
        factory.createText(resources.getMessage(`freeSpinCounterOf`), this.spec.freeSpinCounterOf, this);

        headerBounds.update();

        this.currentValue = factory.createText(``, this.spec.freeSpinCounterCurrent, this);
        this.totalValue = factory.createText(``, this.spec.freeSpinCounterTotal, this);

        this.visible = false;
    }

    setFreeSpins(remaining: number, total: number): void {

        const {resources} = this.services;

        this.currentValue.text = resources.getMessage(`freeSpinCounterCurrent`, {current: `${total - remaining}`});
        this.totalValue.text = resources.getMessage(`freeSpinCounterTotal`, {total: `${total}`});

        this.scaleToFitText(this.currentValue);
        this.scaleToFitText(this.totalValue);
    }

    countUpToTotalFreeSpins(remaining: number, total: number): void {

        this.visible = true;

        const {resources} = this.services;

        const countUpPerDigitDelay = resources.getSetting(`freeSpinCounter`).countUpPerDigitDelay;
        this.currentValue.text = resources.getMessage(`freeSpinCounterCurrent`, {current: `${total - remaining}`});
        this.scaleToFitText(this.currentValue);

        const currentFreeSpinTotal = this.totalValue.text === `` ? 0 : parseInt(this.totalValue.text);
        const tween = Tween.get(this);

        for (let i=currentFreeSpinTotal; i<=total; i++) {

            tween.call(() => {
                this.totalValue.text = this.services.resources.getMessage(`freeSpinCounterTotal`, {total: `${i}`});
                this.scaleToFitText(this.totalValue);
                this.signals.totalCounterIncrement.dispatch();
            });

            if (i === total) {
                tween.call(() => this.signals.totalCountUpComplete.dispatch());
            } else {
                tween.wait(countUpPerDigitDelay);
            }
        }
    }

    skip(total: number): void {

        Tween.removeTweens(this);

        this.totalValue.text = this.services.resources.getMessage(`freeSpinCounterTotal`, {total: `${total}`});
        this.signals.totalCountUpComplete.dispatch();
    }

    resize(orientation: Orientation): void {

        const {factory} = this.services;

        if (orientation === `landscape`) {
            factory.applySpec(this, this.spec.freeSpinCounterLandscape);
        } else {
            factory.applySpec(this, this.spec.freeSpinCounterPortrait);
        }
    }

    override reset(): void {

        Tween.removeTweens(this);

        this.currentValue.text = ``;
        this.totalValue.text = ``;

        this.visible = false;
    }

    private scaleToFitText(text: Text | AdvancedText): void {

        if (text.text.length === 3) {
            text.scale.set(this.threeDigitScale, this.threeDigitScale);
        } else {
            text.scale.set(1, 1);
        }
    }
}